<template>
  <a-modal
    :open="visible"
    :title="$t('certificate_detail_title')"
    :width="isMobileView ? '100%' : 960"
    @cancel="handleCancel"
    :footer="null"
    class="certificate-detail-modal"
    :keyboard="true"
    :maskClosable="true"
    :destroyOnClose="true"
    :focusTriggerAfterClose="false"
    :autofocus="false"
    :wrapClassName="'certificate-detail-modal-wrap cert-detail-overlay'"
    :z-index="isMobileView ? 1100 : undefined"
    :getPopupContainer="getPopupContainer"
  >
    <a-spin :spinning="loading">
      <div class="detail-container">
        <div class="detail-content">
          <!-- 환자 정보 섹션 -->
          <div class="detail-section">
            <h3 class="section-title">
              <i class="fas fa-user-circle"></i>
              {{ $t('certificate_detail_customer_info') }}
            </h3>
            <a-descriptions bordered>
              <a-descriptions-item :label="$t('certificate_detail_name')" :span="2">
                {{ certificateDetail?.customerInfo?.name || '-' }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('certificate_detail_mobile')" :span="1">
                {{ formatPhoneNumber(certificateDetail?.customerInfo?.mobile) }}
              </a-descriptions-item>
            </a-descriptions>
          </div>

          <!-- 제품 정보 섹션 -->
          <div class="detail-section">
            <h3 class="section-title">
              <i class="fas fa-box"></i>
              {{ $t('certificate_detail_product_info') }}
            </h3>
            <a-table
              :columns="productColumns"
              :data-source="certificateDetail?.products || []"
              size="small"
              :pagination="false"
              :scroll="{ x: 900 }"
              class="product-table"
            />
          </div>

          <!-- 시술 정보 섹션 -->
          <div class="detail-section">
            <h3 class="section-title">
              <i class="fas fa-hospital"></i>
              {{ $t('certificate_detail_surgery_info') }}
            </h3>
            <a-descriptions bordered>
              <a-descriptions-item :label="$t('certificate_detail_surgery_area')" :span="1">
                {{ certificateDetail?.surgeryArea?.value || '-' }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('certificate_detail_surgery_date')" :span="2">
                {{ formatDateOnly(certificateDetail?.surgeryAt) }}
              </a-descriptions-item>
            </a-descriptions>
          </div>

          <!-- 인증서 정보 섹션 -->
          <div class="detail-section">
            <h3 class="section-title">
              <i class="fas fa-certificate"></i>
              {{ $t('certificate_detail_certificate_info') }}
            </h3>
            <a-descriptions bordered>
              <a-descriptions-item :label="$t('certificate_detail_issue_date')" :span="2">
                {{ formatDate(certificateDetail?.createAt) }}
              </a-descriptions-item>
              <a-descriptions-item :span="1">
                <template #label>
                  {{ $t('certificate_detail_issue_status') }}<issue-status-help />
                </template>
                {{ formatIssueStatus(certificateDetail?.issueStatus) }}
              </a-descriptions-item>
            </a-descriptions>
          </div>
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import { oneBtnDialog } from '@/utils/utils';
import certificateApis from '../api';
import chinaInapi from '@/apis/chinaInapi';
import {RouterEnum} from "@/enums/router.enums";
import Swal from 'sweetalert2';
import productListApis from '../../Products/api';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import IssueStatusHelp from './IssueStatusHelp.vue';

export default {
  name: 'CertificateDetailModal',
  components: { IssueStatusHelp },
  mixins: [dealerEcountListMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    certificateId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      certificateDetail: null,
      apis: null,
      productApis: null,
      productColumns: [
        {
          title: this.$t('certificate_detail_product_image'),
          dataIndex: 'image',
          key: 'image',
          width: '10%',
          customRender: ({ record }) => (
            <a-image
              src={record.image}
              alt={record.name}
              preview={{
                mask: <div style="color: white">查看大图</div>,
                maskClassName: 'custom-mask'
              }}
              height={60}
              style={{
                objectFit: 'contain',
                borderRadius: '4px',
                border: '1px solid #f0f0f0'
              }}
            />
          )
        },
        {
          title: this.$t('certificate_detail_product_name'),
          dataIndex: 'name',
          key: 'name',
          width: '15%'
        },
        {
          title: this.$t('certificate_detail_spec'),
          dataIndex: 'metadataInfo',
          key: 'spec',
          width: '15%',
          customRender: ({ record }) => {
            const spec = record.metadataInfo?.attributes?.find(attr => attr.trait_type === '规格型号');
            return spec?.value || '-';
          }
        },
        {
          title: this.$t('certificate_detail_tooth_number'),
          dataIndex: 'surgeryArea',
          key: 'toothNumber',
          width: '10%',
          customRender: ({ record }) => record.surgeryArea?.value || '-'
        },
        {
          title: this.$t('certificate_detail_serial_number'),
          dataIndex: 'productCode',
          key: 'serialNo',
          width: '15%',
          align: 'center',
          customRender: ({ record }) => {
            if (record?.productCode) {
              return (
                <div style="display: flex;flex-direction: column; align-items: center;">
                  <span>{record.productCode}</span>
                  <a-button type="default" size="small" style="margin-left: 8px; width: 60px;height: 24px !important; font-size: 12px;" onClick={() => this.goToProductList(record)}>查看产品</a-button>
                </div>
              );
            }
            return '-';
          }
        },
        {
          title: this.$t('certificate_detail_trace_info'),
          dataIndex: 'metadataInfo',
          key: 'traceInfo',
          width: '25%',
          customRender: ({ record }) => {
            const traceInfo = record.metadataInfo?.attributes?.find(attr => attr.trait_type === '追溯信息');
            if (traceInfo?.value && Array.isArray(traceInfo.value)) {
              return (
                <a-timeline>
                  {traceInfo.value.map((item, index) => (
                    <a-timeline-item key={index}>
                      <div class="trace-info-item">
                        <span class="trace-info-title">{item.title}</span>
                      </div>
                    </a-timeline-item>
                  ))}
                </a-timeline>
              );
            }
            return '-';
          }
        },
        {
          title: '操作',
          key: 'action',
          width: '10%',
          align: 'center',
          customRender: ({ record }) => {
            if (record?.productCode && record.productCode !== '-') {
              return (
                <a-button 
                  type="primary" 
                  danger
                  size="small"
                  onClick={() => this.handleRecoverSingleCert(record.productCode)}
                >
                  回收认证卡
                </a-button>
              );
            }
            return '-';
          }
        }
      ]
    };
  },
  created() {
    // @MX:NOTE: china-inapi 이관 완료 — 두 팩토리 모두 chinaInapi 인스턴스를 주입한다.
    //   certificateApis: SPEC §5.5 동결 경로(products/admin/dentium/china/certifications[/received]).
    //   productListApis: trace/recover-certificate 는 china-inapi 에 이미 존재(ProductsList.vue 와 동일 주입).
    //   (SPEC-CHINA-ADMIN-CERT-FOLLOWER-EP-001 M3, 2026-07-27)
    this.apis = certificateApis(chinaInapi);
    this.productApis = productListApis(chinaInapi);
  },
  watch: {
    visible(newVal) {
      if (newVal && this.certificateId) {
        this.fetchCertificateDetail();
      }
    }
  },
  methods: {
    async fetchCertificateDetail() {
      try {
        this.loading = true;
        const response = await this.apis.getCertificateDetail(this.certificateId);
        
        console.log('Detail response:', response.data);
        
        if (response.data?.resultMessage) {
          const [detail] = response.data.resultMessage;
          if (detail) {
            this.certificateDetail = {
              customerInfo: {
                name: detail.customerName,
                mobile: detail.customerMobile
              },
              products: Array.isArray(detail.products) ? detail.products : [{
                name: detail.itemName,
                code: detail.itemCode,
                serialNo: detail.productCode
              }],
              surgeryArea: {
                value: detail.surgeryArea?.value || detail.surgeryArea
              },
              surgeryAt: detail.surgeryAt,
              companyName: detail.companyName,
              createAt: detail.createAt,
              issueStatus: detail.issueStatus
            };
          }
        }
      } catch (error) {
        console.error('Error fetching certificate detail:', error);
        await oneBtnDialog(
          this.$t('certificate_detail_search_fail'),
          this.$t('certificate_detail_search_fail_content'),
          false
        );
      } finally {
        this.loading = false;
      }
    },
    handleCancel() {
      console.log('Modal closing, emitting refresh event');
      this.$emit('update:visible', false);
      this.certificateDetail = null;
      this.$emit('refresh');
      const container = document.querySelector('.page-container-v2');
      if (container) {
        container.focus();
      }
    },
    // 발급 시각 — 중국시간(UTC+8). CertificateList.vue formatDate 와 동일 규약.
    formatDate(date) {
      if (!date) return '-';
      return dayjs.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm');
    },
    // 수술일 — 날짜 전용 값, 저장된 달력 날짜만 표시.
    formatDateOnly(date) {
      if (!date) return '-';
      return dayjs.utc(date).format('YYYY-MM-DD');
    },
    formatPhoneNumber(phone) {
      if (!phone) return '-';
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    },
    // 위챗 알림 발송 상태 포맷팅 (china-inapi IssueStatus 6단계: 발급 자체는 모든 상태에서 완료된 상태)
    formatIssueStatus(status) {
      const statusMap = {
        'PENDING': this.$t('certificate_detail_issue_status_pending'),
        'SAVED': this.$t('certificate_detail_issue_status_saved'),
        'COUNTED': this.$t('certificate_detail_issue_status_counted'),
        'NOTIFIED': this.$t('certificate_detail_issue_status_notified'),
        'NOTIFY_FAILED': this.$t('certificate_detail_issue_status_notify_failed'),
        'SUPPRESSED': this.$t('certificate_detail_issue_status_suppressed')
      };
      return statusMap[status] || status;
    },
    getPopupContainer() {
      return document.querySelector('.certificate-detail-modal') || document.body;
    },
    // 제품 목록 페이지로 이동
    goToProductList(record) {
      this.$emit('update:visible', false);

      this.$router.push({
        path: RouterEnum.ProductManagement.path,
        query: {
          searchFilter: 'serialNo',
          condition: record.productCode
        }
      })
    },
    // 단일 인증서 회수 처리
    async handleRecoverSingleCert(serialNo) {
      try {
        // 확인 대화상자 표시
        const confirmResult = await Swal.fire({
          title: '确认回收认证卡',
          text: `确定要回收序列号为 ${serialNo} 的产品认证卡吗？`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-primary-confirm btn-hover swal-button-custom',
            cancelButton: 'btn btn-secondary-cancel btn-hover swal-button-custom',
          }
        });

        if (confirmResult.isConfirmed) {
          this.loading = true;
          
          // API 호출
          const response = await this.productApis.recoverCertificate(serialNo);
          
          if (response.data && response.data.success) {
            await Swal.fire({
              title: '操作成功',
              text: '认证卡回收成功',
              icon: 'success',
              confirmButtonText: '确认',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary btn-hover',
              }
            });
            
            // 부모 컴포넌트에 새로고침 이벤트 전달
            this.$emit('refresh');
            
            // 현재 모달 내용 재조회
            await this.fetchCertificateDetail();
            
            // 인증서가 하나도 없으면 모달 닫기
            if (!this.certificateDetail || 
                !this.certificateDetail.products || 
                this.certificateDetail.products.length === 0 ||
                !this.certificateDetail.products.some(p => p.productCode && p.productCode !== '-')) {
              this.$emit('update:visible', false);
              this.certificateDetail = null;
            }
          } else {
            await Swal.fire({
              title: '操作失败',
              text: response?.data?.message || '认证卡回收失败，请稍后重试',
              icon: 'error',
              confirmButtonText: '确认',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary btn-hover',
              }
            });
          }
        }
      } catch (error) {
        console.error('Error recovering certificate:', error);
        await Swal.fire({
          title: '操作失败',
          text: error.response?.data?.message || error.message || '认证卡回收失败，请稍后重试',
          icon: 'error',
          confirmButtonText: '确认',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-primary btn-hover',
          }
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.certificate-detail-modal {
  :deep(.ant-modal-content) {
    border-radius: 8px;
    
    .ant-modal-header {
      border-radius: 8px 8px 0 0;
      background-color: #f8f9fa;
      padding: 16px 24px;
      
      .ant-modal-title {
        font-size: 18px;
        font-weight: 600;
      }
    }
    
    .ant-modal-body {
      padding: 0;
    }
  }
}

.detail-container {
  outline: none;
  position: relative;

  .detail-content {
    padding: 24px;
  }

  .detail-section {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #1f2937;
      display: flex;
      align-items: center;
      
      i {
        margin-right: 8px;
        color: #3b82f6;
      }
    }
  }

  :deep(.ant-descriptions) {
    background-color: #fff;
    
    .ant-descriptions-item-label {
      width: 120px;
      background-color: #f8f9fa;
      color: #4b5563;
      font-weight: 500;
    }
    
    .ant-descriptions-item-content {
      color: #1f2937;
    }
  }

  .product-table {
    :deep(.ant-table) {
      border-radius: 8px;
      
      .ant-table-thead > tr > th {
        background-color: #f8f9fa;
        color: #4b5563;
        font-weight: 600;
        white-space: nowrap;
      }
      
      .ant-table-tbody > tr > td {
        vertical-align: middle;
        padding: 12px 8px;
        
        &:first-child {
          padding: 8px;
          text-align: center;
          
          .ant-image {
            display: inline-block;
            
            img {
              transition: transform 0.3s ease;
              
              &:hover {
                transform: scale(1.05);
              }
            }
          }
        }
        
        &:last-child {
          white-space: normal;
          word-break: break-word;
          
          .ant-timeline {
            margin-top: -8px;
            
            .ant-timeline-item {
              padding-bottom: 8px;
              
              &:last-child {
                padding-bottom: 0;
              }
              
              .ant-timeline-item-content {
                margin-left: 20px;
                min-height: auto;
              }
            }
          }
          
          .trace-info-item {
            .trace-info-title {
              color: #1f2937;
              font-size: 13px;
            }
          }
        }
      }
    }
  }
}

:deep(.ant-modal-body) {
  max-height: calc(95vh - 110px);
  overflow-y: auto;
}

:deep(.ant-image-preview-mask) {
  background-color: rgba(0, 0, 0, 0.85);
}

:deep(.ant-image-preview-wrap) {
  .ant-image-preview-operations {
    padding: 20px;
  }
  
  .ant-image-preview-img {
    transition: transform 0.3s ease;
  }
}
</style>

<!-- 모달은 document.body로 teleport되므로 scoped 스타일이 닿지 않는다(house precedent: IamConsole.vue).
     wrapClassName으로 부여한 .cert-detail-overlay로 스코프를 좁혀 다른 화면에 영향이 없도록 한다
     (모바일 앱헤더 z-index 1020보다 아래 깔리는 문제 대응). -->
<style>
@media (max-width: 767px) {
  .ant-modal-wrap.cert-detail-overlay {
    z-index: 1100;
  }

  .ant-modal-wrap.cert-detail-overlay ~ .ant-modal-mask {
    z-index: 1100;
  }

  .cert-detail-overlay .ant-modal {
    max-width: calc(100vw - 24px);
    margin: 8px auto;
  }

  .cert-detail-overlay .ant-modal-body {
    max-height: 70vh;
    max-height: 70dvh;
    overflow-y: auto;
  }
}
</style>
